import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Link } from "react-router-dom";

import isEmpty from "lodash/isEmpty";
import { IoArrowRedo, IoInformationCircleOutline } from "react-icons/io5";
import { Tooltip as ReactTooltip } from "react-tooltip";

import View from "../styled/View";
import Avatar from "../styled/Avatar";
import Badge from "../common/Badge";
import FileUpload from "../common/FileUpload";

import { formatDate } from "../../helpers/date";
import { capitalizeEveryWord, generateUniqueId } from "../../utils";

const ProjectView = ({ project, auth, uploadFile, downloadFile, loading }) => {
  const data = {};
  const resource = {};

  if (!isEmpty(project)) {
    // project info
    data["Project ID"] = project.project_number;
    data["Call Type"] = project.call_type.label;
    data.Name = project.name;
    data.Description = project.description;
    data.Customer = project.customer.name;
    data.Contract = project.contract && project.contract.name;
    data["Start Date"] = formatDate(project.start_date, "MM/dd/yyyy");
    data["End Date"] =
      project.end_date && formatDate(project.end_date, "MM/dd/yyyy");
    data.Category = capitalizeEveryWord(project?.category?.value || "");
    data["Billing Rate"] = `$${project.rate}`;
    data["Primary Department"] = project.department?.description;
    data["Department Category"] = project?.department_category?.name;
    data["See SOW"] = project.sowAttachmentUrl;
    // resource info
    resource["Project Manager"] =
      project.project_manager && project.project_manager;
    resource["Sales Person"] = project.salesperson && project.salesperson;
    resource["Primary Resource"] = project.resource1 && project.resource1;
    resource["Secondary Resource"] = project.resource2 && project.resource2;
  }

  const supportResources = (project.supportResources || []).sort(
    (a, b) => a.order - b.order
  );

  const supportResourcesToDisplay = supportResources.slice(0, 4);
  const supportResourcesForTooltip = supportResources.slice(4);

  return (
    <ProjectViewStyles
      columns={
        supportResourcesToDisplay?.length > 0 ? "2fr 1fr 1fr" : "2fr 1fr"
      }
    >
      {/* Project Info */}
      <div className="content-wrapper">
        <fieldset>
          {!isEmpty(project) && (
            <legend>
              Project Info
              <Badge
                className="status-badge"
                color={project.project_status.color}
              >
                {project.project_status.label}
              </Badge>
            </legend>
          )}
        </fieldset>
        <div className="data-container">
          {Object.keys(data)
            .filter((key) => data[key])
            .map((key, i) =>
              key !== "Contract" && key !== "See SOW" ? (
                <div key={i} className="data">
                  <label htmlFor="contract">{key}</label>
                  <span id="contract">{data[key]}</span>
                </div>
              ) : (
                <div key={i} className="data">
                  <label htmlFor="link">
                    {key}
                    <IoArrowRedo style={{ paddingLeft: "3px" }} />
                  </label>
                  {key === "See SOW" && project.sowAttachmentUrl ? (
                    <Link
                      onClick={() => {
                        downloadFile(project.sowAttachmentUrl);
                      }}
                      style={{ fontWeight: "700" }}
                    >
                      Download
                    </Link>
                  ) : (
                    <Link
                      to={`/app/contracts/${project.contract._id}`}
                      style={{ fontWeight: "700" }}
                    >
                      {data[key]}
                    </Link>
                  )}
                </div>
              )
            )}
        </div>
        <fieldset>
          <legend>Upload SOW</legend>
        </fieldset>
        <FileUpload
          auth={auth}
          onDrop={uploadFile}
          disabled={loading}
          multiple={false}
        />
      </div>
      {/* Resource Info */}
      <div className="content-wrapper">
        <fieldset>
          <legend>Resource Info</legend>
        </fieldset>
        <div className="data-container-single">
          {Object.keys(resource)
            .filter((key) => resource[key])
            .map((key, i) => (
              <div key={i} className="data">
                <label htmlFor={resource[key].full_name}>{key}</label>
                <div className="flex -center-v" style={{ marginTop: "5px" }}>
                  <Avatar
                    user={resource[key]}
                    avatar={resource[key].avatar}
                    size="xs"
                  />
                  <span style={{ marginLeft: 5 }}>
                    {resource[key].full_name}
                  </span>
                </div>
              </div>
            ))}
        </div>
      </div>
      {/* Support Resources Info */}
      {supportResourcesToDisplay?.length > 0 && (
        <div className="content-wrapper">
          <fieldset>
            <legend>Support Resources</legend>
          </fieldset>
          <div className="data-container-single">
            {supportResourcesToDisplay.map((r) => (
              <div key={r._id} className="data">
                <label htmlFor={r.employee.full_name}>
                  Support Resource #{r.order}
                </label>
                <div className="flex -center-v" style={{ marginTop: "5px" }}>
                  <Avatar
                    user={r.employee}
                    avatar={r.employee.avatar}
                    size="xs"
                  />
                  <span style={{ marginLeft: 5 }}>{r.employee.full_name}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="data-container-single">
            {!!supportResourcesForTooltip.length && (
              <React.Fragment>
                <div
                  id="more-support-resouces"
                  className="data"
                  style={{ flexDirection: "row", gap: 5 }}
                  data-tooltip-variant={auth?.isNightMode ? "dark" : "light"}
                >
                  <label>Other Support Resources</label>
                  <IoInformationCircleOutline color="#aaa" />
                </div>
                <ReactTooltip
                  id="more-support-resouces"
                  anchorId="more-support-resouces"
                  place="left"
                  className={`custom-tooltip ${
                    auth?.isNightMode ? "dark" : "light"
                  }`}
                >
                  {supportResourcesForTooltip.map((r) => (
                    <p
                      key={generateUniqueId()}
                    >{`#${r.order}: ${r.employee.full_name}`}</p>
                  ))}
                </ReactTooltip>
              </React.Fragment>
            )}
          </div>
        </div>
      )}
    </ProjectViewStyles>
  );
};

const ProjectViewStyles = styled(View)`
  grid-template-columns: ${(props) => props.columns};
  @media screen and (max-width: 769px) {
    grid-template-columns: 1fr !important;
    margin-top: 15px;
    .status-badge {
      display: inline !important;
      right: 15px !important;
      position: absolute !important;
      top: 15px !important;
    }
  }
`;

ProjectView.propTypes = {
  project: PropTypes.object.isRequired,
  auth: PropTypes.object,
  uploadFile: PropTypes.func,
  downloadFile: PropTypes.func,
  loading: PropTypes.bool
};

export default ProjectView;
